import {
  CogIcon,
  EnvelopeIcon,
  ExclamationTriangleIcon,
  GlobeAltIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { Button } from '@heroui/button';
import { Card, CardBody, CardHeader } from '@heroui/card';
import { Divider } from '@heroui/divider';
import { Select, SelectItem } from '@heroui/select';
import { Switch } from '@heroui/switch';
import React, { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import SEOHead from '../../components/SEOHead';
import { useLocale } from '../../contexts/LocaleContext';
import { authService } from '../../services/authService';
import { orderService } from '../../services/orderService';

const currencyOptions = [
  { key: 'GBP', label: 'GBP (£)' },
  { key: 'USD', label: 'USD ($)' },
  { key: 'EUR', label: 'EUR (€)' },
  { key: 'CAD', label: 'CAD ($)' },
];

const timezoneOptions = [
  { key: 'America/New_York', label: 'Eastern Time (UTC-5)' },
  { key: 'America/Chicago', label: 'Central Time (UTC-6)' },
  { key: 'America/Denver', label: 'Mountain Time (UTC-7)' },
  { key: 'America/Los_Angeles', label: 'Pacific Time (UTC-8)' },
  { key: 'Europe/London', label: 'London Time (UTC+0)' },
  { key: 'Europe/Paris', label: 'Central European Time (UTC+1)' },
  { key: 'Asia/Tokyo', label: 'Japan Time (UTC+9)' },
];

const Settings: React.FC = () => {
  const pageTitle = 'Account Settings';
  const pageDescription = 'Manage preferences, locale, and notifications.';
  const { currentLocale, updateLocale } = useLocale();
  const [hasOutstandingOrders, setHasOutstandingOrders] =
    useState<boolean>(false);

  // Settings state - synced with locale and backend APIs
  const [settings, setSettings] = useState({
    emailNotifications: {
      orderUpdates: true,
      promotions: false,
      newsletter: true,
      securityAlerts: true,
    },
    preferences: {
      timezone: 'Europe/London',
      currency: currentLocale.preferredCurrency,
    },
  });

  useEffect(() => {
    // Sync currency with global locale
    setSettings(prev => ({
      ...prev,
      preferences: {
        ...prev.preferences,
        currency: currentLocale.preferredCurrency,
      },
    }));
  }, [currentLocale.preferredCurrency]);

  useEffect(() => {
    const fetchOutstanding = async () => {
      try {
        const orders = await orderService.request<any[]>({
          url: '/order/current',
          method: 'GET',
        });
        const hasUnshipped = (orders || []).some((o: any) => {
          const s = (o.status || '').toString().toLowerCase();
          return (
            s === 'pending' ||
            s === 'processing' ||
            s === 'printing' ||
            s === 'printed'
          );
        });
        setHasOutstandingOrders(hasUnshipped);
      } catch {
        setHasOutstandingOrders(false);
      }
    };
    fetchOutstanding();
  }, []);

  useEffect(() => {
    // Load existing email preferences from backend on mount
    const loadPreferences = async () => {
      try {
        const prefs = await authService.request<any>({
          url: '/auth/email-preferences',
          method: 'GET',
        });
        setSettings(prev => ({
          ...prev,
          emailNotifications: {
            orderUpdates:
              prefs?.orderUpdates ?? prev.emailNotifications.orderUpdates,
            promotions: prefs?.promotions ?? prev.emailNotifications.promotions,
            newsletter: prefs?.newsletter ?? prev.emailNotifications.newsletter,
            securityAlerts:
              prefs?.securityAlerts ?? prev.emailNotifications.securityAlerts,
          },
        }));
      } catch {
        // ignore
      }
    };
    loadPreferences();
  }, []);

  const handleNotificationChange = (
    category: string,
    setting: string,
    value: boolean
  ) => {
    setSettings(prev => ({
      ...prev,
      emailNotifications: {
        ...prev.emailNotifications,
        [setting]: value,
      },
    }));

    // Persist to backend
    authService
      .request({
        url: '/auth/email-preferences',
        method: 'PUT',
        data: {
          orderUpdates:
            category === 'emailNotifications' && setting === 'orderUpdates'
              ? value
              : settings.emailNotifications.orderUpdates,
          promotions:
            category === 'emailNotifications' && setting === 'promotions'
              ? value
              : settings.emailNotifications.promotions,
          newsletter:
            category === 'emailNotifications' && setting === 'newsletter'
              ? value
              : settings.emailNotifications.newsletter,
          securityAlerts:
            category === 'emailNotifications' && setting === 'securityAlerts'
              ? value
              : settings.emailNotifications.securityAlerts,
        },
      })
      .then(() => toast.success('Settings updated'))
      .catch(() => toast.error('Failed to save settings'));
  };

  const handlePreferenceChange = (setting: string, value: string) => {
    setSettings(prev => ({
      ...prev,
      preferences: {
        ...prev.preferences,
        [setting]: value,
      },
    }));

    if (setting === 'currency') {
      updateLocale({ preferredCurrency: value });
    }

    toast.success('Preference updated');
  };

  const handleDeleteAccount = () => {
    if (
      window.confirm(
        'Are you sure you want to delete your account? This action cannot be undone.'
      )
    ) {
      // In a real app, you would call an API to delete the account
      toast.error('Account deletion is not implemented in this demo');
    }
  };

  return (
    <>
      <SEOHead title={pageTitle} description={pageDescription} />
      <div className='space-y-6'>
        {/* Email Notifications */}
        <Card>
          <CardHeader>
            <div className='flex items-center space-x-3'>
              <EnvelopeIcon className='w-6 h-6 text-primary-600' />
              <div>
                <h2 className='text-xl font-semibold'>Email Notifications</h2>
                <p className='text-gray-600'>
                  Choose what emails you'd like to receive
                </p>
              </div>
            </div>
          </CardHeader>
          <CardBody className='space-y-4'>
            <div className='flex items-center justify-between'>
              <div>
                <h3 className='font-medium'>Order Updates</h3>
                <p className='text-sm text-gray-600'>
                  Get notified about order status changes
                </p>
              </div>
              <Switch
                isSelected={settings.emailNotifications.orderUpdates}
                onValueChange={value =>
                  handleNotificationChange(
                    'emailNotifications',
                    'orderUpdates',
                    value
                  )
                }
              />
            </div>

            <Divider />

            <div className='flex items-center justify-between'>
              <div>
                <h3 className='font-medium'>Promotions & Offers</h3>
                <p className='text-sm text-gray-600'>
                  Receive special offers and discounts
                </p>
              </div>
              <Switch
                isSelected={settings.emailNotifications.promotions}
                onValueChange={value =>
                  handleNotificationChange(
                    'emailNotifications',
                    'promotions',
                    value
                  )
                }
              />
            </div>

            <Divider />

            <div className='flex items-center justify-between'>
              <div>
                <h3 className='font-medium'>Newsletter</h3>
                <p className='text-sm text-gray-600'>
                  Weekly updates about new products and features
                </p>
              </div>
              <Switch
                isSelected={settings.emailNotifications.newsletter}
                onValueChange={value =>
                  handleNotificationChange(
                    'emailNotifications',
                    'newsletter',
                    value
                  )
                }
              />
            </div>

            <Divider />

            <div className='flex items-center justify-between'>
              <div>
                <h3 className='font-medium'>Security Alerts</h3>
                <p className='text-sm text-gray-600'>
                  Important security notifications (recommended)
                </p>
              </div>
              <Switch
                isSelected={settings.emailNotifications.securityAlerts}
                onValueChange={value =>
                  handleNotificationChange(
                    'emailNotifications',
                    'securityAlerts',
                    value
                  )
                }
              />
            </div>
          </CardBody>
        </Card>

        {/* Push Notifications - removed */}

        {/* Privacy - removed */}

        {/* Preferences */}
        <Card>
          <CardHeader>
            <div className='flex items-center space-x-3'>
              <GlobeAltIcon className='w-6 h-6 text-primary-600' />
              <div>
                <h2 className='text-xl font-semibold'>Preferences</h2>
                <p className='text-gray-600'>Customize your experience</p>
              </div>
            </div>
          </CardHeader>
          <CardBody className='space-y-4'>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-2'>
                  Currency
                </label>
                <Select
                  selectedKeys={[settings.preferences.currency]}
                  items={currencyOptions}
                  onSelectionChange={keys => {
                    const selected = Array.from(keys)[0] as string;
                    handlePreferenceChange('currency', selected);
                  }}
                >
                  {option => (
                    <SelectItem key={option.key}>{option.label}</SelectItem>
                  )}
                </Select>
              </div>
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-2'>
                  Timezone
                </label>
                <Select
                  selectedKeys={[settings.preferences.timezone]}
                  items={timezoneOptions}
                  onSelectionChange={keys => {
                    const selected = Array.from(keys)[0] as string;
                    handlePreferenceChange('timezone', selected);
                  }}
                >
                  {option => (
                    <SelectItem key={option.key}>{option.label}</SelectItem>
                  )}
                </Select>
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Data Management */}
        <Card>
          <CardHeader>
            <div className='flex items-center space-x-3'>
              <CogIcon className='w-6 h-6 text-primary-600' />
              <div>
                <h2 className='text-xl font-semibold'>Data Management</h2>
                <p className='text-gray-600'>Manage your personal data</p>
              </div>
            </div>
          </CardHeader>
          <CardBody className='space-y-4'>
            <div className='flex items-center justify-between p-4 border border-danger-200 rounded-lg bg-danger-50'>
              <div className='flex items-start space-x-3'>
                <ExclamationTriangleIcon className='w-6 h-6 text-danger-600 mt-0.5 flex-shrink-0' />
                <div>
                  <h3 className='font-medium text-danger-900'>
                    Delete Account
                  </h3>
                  <p className='text-sm text-danger-700'>
                    Permanently delete your account and all associated data.
                    This action cannot be undone.
                  </p>
                </div>
              </div>
              <Button
                color='danger'
                variant='bordered'
                startContent={<TrashIcon className='w-4 h-4' />}
                onClick={handleDeleteAccount}
                isDisabled={hasOutstandingOrders}
              >
                Delete Account
              </Button>
            </div>
          </CardBody>
        </Card>
        {/* Account Information - removed */}
      </div>
    </>
  );
};

export default Settings;
